import * as Yup from "yup";

// Comprehensive User Validation Schema
export const createUserValidationSchema = (isInternal: boolean = true) =>
  Yup.object({
    fullName: Yup.string()
      .min(3, "Full name must be at least 3 characters")
      .max(100, "Full name cannot exceed 100 characters")
      .matches(
        /^[a-zA-Z\s\.]+$/,
        "Full name can only contain letters, spaces, and dots"
      )
      .required("Full name is required"),

    userName: Yup.string()
      .min(3, "Username must be at least 3 characters")
      .max(50, "Username cannot exceed 50 characters")
      .matches(
        /^[a-zA-Z0-9_]+$/,
        "Username can only contain letters, numbers, and underscores"
      )
      .required("Username is required"),

    email: Yup.string()
      .email("Please enter a valid email address")
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Invalid email format"
      )
      .required("Email is required"),

    role: Yup.string().required("Role is required"),

    tokenLogin: Yup.string()
      .min(6, "Token login must be at least 6 characters")
      .max(50, "Token login cannot exceed 50 characters")
      .matches(
        /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/,
        "Token can contain letters, numbers, and special characters"
      )
      .test(
        "uppercase-required",
        "Token login must contain at least 1 uppercase letter",
        function (value) {
          if (!value) return true; // Let required validation handle empty values
          return /[A-Z]/.test(value);
        }
      )
      .test(
        "special-char-required",
        "Token login must contain at least 1 special character",
        function (value) {
          if (!value) return true; // Let required validation handle empty values
          return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);
        }
      )
      .required("Token login is required"),

    tokenApproval: Yup.string()
      .min(6, "Token approval must be at least 6 characters")
      .max(50, "Token approval cannot exceed 50 characters")
      .matches(
        /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/,
        "Token can contain letters, numbers, and special characters"
      )
      .required("Token approval is required"),

    merchantId: Yup.string().when("role", {
      is: (val: string) => isInternal && val?.startsWith("MERCHANT"),
      then: (schema) =>
        schema.required("Merchant is required for MERCHANT roles"),
      otherwise: (schema) => schema.nullable(),
    }),
  });

// Initial Values for User Form
export const userInitialValues = {
  fullName: "",
  userName: "",
  email: "",
  merchantId: null as string | null | undefined,
  role: "",
  tokenLogin: "",
  tokenApproval: "",
  isActive: true,
};

// TypeScript Interface for User Form Values
export interface UserFormValues {
  fullName: string;
  userName: string;
  email: string;
  merchantId: string | null | undefined;
  role: string;
  tokenLogin: string;
  tokenApproval: string;
  isActive: boolean;
}
